import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button, Input, Select, Checkbox, Field } from "@/components/ds";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { SizeChartTable } from "@/components/catalogue/SizeChartTable";

const WASHES = ["Indigo", "Black / Black OD", "Stone wash", "Enzyme wash", "Raw / Rinse"];
const BADGES = ["", "Bestseller", "New", "Low MOQ"];
const CATEGORIES = ["Denim jeans", "Denim jackets", "Denim shorts", "Chinos & trousers"];
const FABRICS = ["Cotton", "Stretchable"];
const COUNTRIES = ["India", "Bangladesh"];
const CATALOGUE_OPTS = [{ value: "both", label: "Both (India + Dhaka)" }, { value: "india", label: "India catalogue" }, { value: "dhaka", label: "Dhaka catalogue" }];
const CAT_LABEL = { both: "Both", india: "India", dhaka: "Dhaka" };
const COP_OPTIONS = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0].map((v) => ({ value: String(v), label: `${v.toFixed(1)} · ${Math.round(v * 100)}% of CP` }));
const MANU_BLANK = { name: "", country: "India", gst: "", bin: "", mobile: "", email: "", address: "" };

const BLANK = {
  code: "", name: "", segment: "Men", category: "Denim jeans", fabric: "Cotton",
  fabric_mill: "", fabric_sort: "",
  weight_oz: "", sizes: "28–40", lead_min: 30, lead_max: 35, price: 485, moq: 300,
  washes: [], badge: "", article: "", images: ["", "", "", ""], manufacturer_id: "", catalogue: "both", source_link: "", size_chart: null,
};
function Panel({ children, style }) {
  return <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-soft)", padding: 20, ...style }}>{children}</div>;
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("catalogue");
  const [skus, setSkus] = useState([]);
  const [leads, setLeads] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [manuForm, setManuForm] = useState(MANU_BLANK);
  const [manuEditingId, setManuEditingId] = useState(null);
  const [manuBusy, setManuBusy] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [chartBusy, setChartBusy] = useState(false);
  const [settings, setSettings] = useState({ india: "0.3", dhaka: "0.3", moq: "150", lead_min: "30", lead_max: "45" });
  const [settingsBusy, setSettingsBusy] = useState(false);

  const uploadPhotos = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const urls = [...form.images];
      for (const f of Array.from(files).slice(0, 4)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const idx = urls.findIndex((u) => !u || !u.trim());
        if (idx === -1) break;
        urls[idx] = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      }
      setForm((prev) => ({ ...prev, images: urls }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
    }
  };

  const parseChart = async (fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    setChartBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/parse-size-chart", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, size_chart: data }));
      toast.success(`Size chart read — ${data.rows.length} sizes`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setChartBusy(false);
    }
  };

  useEffect(() => { if (ready && !user) nav("/admin/login"); }, [ready, user, nav]);

  const loadSkus = useCallback(() => {
    api.get("/admin/skus").then((r) => setSkus(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const loadLeads = useCallback(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const loadManu = useCallback(() => {
    api.get("/admin/manufacturers").then((r) => setManufacturers(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const loadSettings = useCallback(() => {
    api.get("/settings").then((r) => setSettings({ india: String(r.data.india), dhaka: String(r.data.dhaka), moq: String(r.data.moq), lead_min: String(r.data.lead_min), lead_max: String(r.data.lead_max) })).catch(() => {});
  }, []);

  useEffect(() => { if (user) { loadSkus(); loadLeads(); loadManu(); loadSettings(); } }, [user, loadSkus, loadLeads, loadManu, loadSettings]);

  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      await api.put("/admin/settings", { india: Number(settings.india), dhaka: Number(settings.dhaka), moq: Number(settings.moq), lead_min: Number(settings.lead_min), lead_max: Number(settings.lead_max) });
      toast.success("Catalogue defaults saved");
      loadSkus();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSettingsBusy(false); }
  };

  if (!ready || !user) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setImg = (i, v) => setForm((f) => { const im = [...f.images]; im[i] = v; return { ...f, images: im }; });
  const toggleWash = (w) => setForm((f) => ({ ...f, washes: f.washes.includes(w) ? f.washes.filter((x) => x !== w) : [...f.washes, w] }));

  const resetForm = () => { setForm(BLANK); setEditingId(null); };

  const editSku = (s) => {
    setEditingId(s.id);
    setForm({
      code: s.code, name: s.name, segment: s.segment, category: s.category, fabric: s.fabric,
      fabric_mill: s.fabric_mill || "", fabric_sort: s.fabric_sort || "",
      weight_oz: s.weight_oz, sizes: s.sizes, lead_min: s.lead_min, lead_max: s.lead_max,
      price: s.price, moq: s.moq, washes: s.washes || [], badge: s.badge || "",
      article: s.article || "", images: [0, 1, 2, 3].map((i) => (s.images?.[i] || "")),
      manufacturer_id: s.manufacturer_id || "",
      weight_oz: s.weight_oz == null ? "" : s.weight_oz,
      catalogue: s.catalogue || "both",
      source_link: s.source_link || "",
      size_chart: s.size_chart || null,
    });
    setTab("catalogue");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Style name is required");
    if (!form.manufacturer_id) return toast.error(manufacturers.length ? "Please select a manufacturer" : "Add a manufacturer first (Manufacturers tab)");
    if (!form.source_link.trim()) return toast.error("Source link is required");
    setBusy(true);
    const payload = {
      ...form,
      weight_oz: form.weight_oz === "" || form.weight_oz == null ? null : Number(form.weight_oz), lead_min: Number(form.lead_min), lead_max: Number(form.lead_max),
      price: Number(form.price), moq: Number(form.moq),
      images: form.images.filter((x) => x.trim()),
      manufacturer_id: form.manufacturer_id || null,
      size_chart: form.size_chart || null,
    };
    try {
      if (editingId) { await api.put(`/admin/skus/${editingId}`, payload); toast.success("SKU updated"); }
      else { await api.post("/admin/skus", payload); toast.success("SKU created"); }
      resetForm(); loadSkus();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const toggleDisabled = async (s) => {
    try {
      await api.patch(`/admin/skus/${s.id}/disabled`, { is_disabled: !s.is_disabled });
      toast.success(s.is_disabled ? `${s.code} is live again` : `${s.code} hidden from catalogue`);
      loadSkus();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.code}?`)) return;
    try { await api.delete(`/admin/skus/${s.id}`); toast.success("Deleted"); loadSkus(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setM = (k, v) => setManuForm((f) => ({ ...f, [k]: v }));
  const resetManu = () => { setManuForm(MANU_BLANK); setManuEditingId(null); };
  const manuById = Object.fromEntries(manufacturers.map((m) => [m.id, m]));

  const editManu = (m) => {
    setManuEditingId(m.id);
    setManuForm({ name: m.name, country: m.country, gst: m.gst || "", bin: m.bin || "", mobile: m.mobile || "", email: m.email || "", address: m.address || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveManu = async () => {
    if (!manuForm.name.trim()) return toast.error("Manufacturer name is required");
    if (!manuForm.mobile.trim()) return toast.error("Mobile is required");
    if (!/^\S+@\S+\.\S+$/.test(manuForm.email)) return toast.error("Valid email is required");
    if (manuForm.country === "India" && !manuForm.gst.trim()) return toast.error("GST is required for India");
    if (manuForm.country === "Bangladesh" && !manuForm.bin.trim()) return toast.error("BIN is required for Bangladesh");
    setManuBusy(true);
    try {
      if (manuEditingId) { await api.put(`/admin/manufacturers/${manuEditingId}`, manuForm); toast.success("Manufacturer updated"); }
      else { await api.post("/admin/manufacturers", manuForm); toast.success("Manufacturer added"); }
      resetManu(); loadManu();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setManuBusy(false); }
  };

  const delManu = async (m) => {
    if (!window.confirm(`Delete manufacturer "${m.name}"? Linked styles will be unlinked.`)) return;
    try { await api.delete(`/admin/manufacturers/${m.id}`); toast.success("Deleted"); loadManu(); loadSkus(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--surface-muted)" }}>
      <header style={{ position: "sticky", top: 0, zIndex: 40, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 32px", background: "var(--surface-card)", borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src="/locofast-logo.png" alt="Locofast" style={{ height: 28 }} />
          <span style={{ width: 1, height: 20, background: "var(--border-default)" }} />
          <span style={{ font: "var(--fw-medium) 14px/1 var(--font-sans)", color: "var(--text-secondary)" }}>Admin</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" style={{ font: "var(--fw-medium) 13px/1 var(--font-sans)" }}>View catalogue ↗</a>
          <Button data-testid="logout-btn" variant="secondary" size="sm" onClick={() => { logout(); nav("/admin/login"); }}>Sign out</Button>
        </div>
      </header>

      <div style={{ maxWidth: 1120, margin: "0 auto", padding: 32, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {[["catalogue", `Catalogue (${skus.length})`], ["manufacturers", `Manufacturers (${manufacturers.length})`], ["leads", `Leads (${leads.length})`]].map(([key, label]) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)} style={{ padding: "8px 16px", borderRadius: "var(--radius-md)", border: "1px solid " + (tab === key ? "var(--lf-blue-600)" : "var(--border-strong)"), background: tab === key ? "var(--lf-blue-600)" : "var(--surface-card)", color: tab === key ? "#fff" : "var(--text-secondary)", cursor: "pointer", font: "var(--fw-semibold) 13px/1 var(--font-sans)" }}>{label}</button>
          ))}
        </div>

        {tab === "catalogue" && (
          <>
            <Panel data-testid="cop-settings">
              <div style={{ font: "var(--fw-semibold) 16px/1.2 var(--font-display)", color: "var(--text-primary)", marginBottom: 6 }}>Catalogue defaults</div>
              <div style={{ font: "var(--fw-regular) 13px/1.4 var(--font-sans)", color: "var(--text-secondary)", marginBottom: 16 }}>Selling price = cost price (CP) × COP factor. MOQ and lead time apply to every style — they are shown on all product cards and in the hero stats.</div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ minWidth: 200 }}><Field label="India COP factor"><Select data-testid="cop-india" options={COP_OPTIONS} value={settings.india} onChange={(e) => setSettings((s) => ({ ...s, india: e.target.value }))} /></Field></div>
                <div style={{ minWidth: 200 }}><Field label="Dhaka COP factor"><Select data-testid="cop-dhaka" options={COP_OPTIONS} value={settings.dhaka} onChange={(e) => setSettings((s) => ({ ...s, dhaka: e.target.value }))} /></Field></div>
                <div style={{ width: 130 }}><Field label="MOQ (pcs) — all"><Input data-testid="global-moq" type="number" min="1" value={settings.moq} onChange={(e) => setSettings((s) => ({ ...s, moq: e.target.value }))} /></Field></div>
                <div style={{ width: 130 }}><Field label="Lead time min (d)"><Input data-testid="global-lead-min" type="number" min="1" value={settings.lead_min} onChange={(e) => setSettings((s) => ({ ...s, lead_min: e.target.value }))} /></Field></div>
                <div style={{ width: 130 }}><Field label="Lead time max (d)"><Input data-testid="global-lead-max" type="number" min="1" value={settings.lead_max} onChange={(e) => setSettings((s) => ({ ...s, lead_max: e.target.value }))} /></Field></div>
                <Button data-testid="save-cop-btn" variant="primary" size="md" onClick={saveSettings} disabled={settingsBusy}>{settingsBusy ? "Saving…" : "Save defaults"}</Button>
              </div>
            </Panel>

            <Panel>
              <div style={{ font: "var(--fw-semibold) 16px/1.2 var(--font-display)", color: "var(--text-primary)", marginBottom: 16 }}>{editingId ? "Edit SKU" : "Add a new SKU"}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 14 }}>
                <Field label="SKU code"><Input data-testid="sku-code" value={form.code} readOnly disabled placeholder={editingId ? "" : "Auto-generated on save"} /></Field>
                <Field label="Style name"><Input data-testid="sku-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Loose Fit Jean" /></Field>
                <Field label="Segment"><Select options={["Men", "Women"]} value={form.segment} onChange={(e) => set("segment", e.target.value)} /></Field>
                <Field label="Category"><Select options={CATEGORIES} value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
                <Field label="Fabric"><Select data-testid="sku-fabric" options={[...new Set([...FABRICS, form.fabric].filter(Boolean))]} value={form.fabric} onChange={(e) => set("fabric", e.target.value)} /></Field>
                <Field label="Fabric mill name — optional"><Input data-testid="sku-fabric-mill" value={form.fabric_mill} onChange={(e) => set("fabric_mill", e.target.value)} placeholder="e.g. Arvind Mills" /></Field>
                <Field label="Fabric sort number — optional"><Input data-testid="sku-fabric-sort" value={form.fabric_sort} onChange={(e) => set("fabric_sort", e.target.value)} placeholder="e.g. ARV-4521" /></Field>
                <Field label="Weight (oz) — optional"><Input data-testid="sku-weight" type="number" step="0.25" value={form.weight_oz} onChange={(e) => set("weight_oz", e.target.value)} placeholder="e.g. 11" /></Field>
                <Field label="Price / pc (₹)"><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} /></Field>
                <Field label="Manufacturer *"><Select data-testid="sku-manufacturer" options={[{ value: "", label: manufacturers.length ? "Select manufacturer…" : "No manufacturers — add one first" }, ...manufacturers.map((m) => ({ value: m.id, label: `${m.name} · ${m.country}` }))]} value={form.manufacturer_id} onChange={(e) => set("manufacturer_id", e.target.value)} /></Field>
                <Field label="Catalogue"><Select data-testid="sku-catalogue" options={CATALOGUE_OPTS} value={form.catalogue} onChange={(e) => set("catalogue", e.target.value)} /></Field>
                <Field label="Source link *"><Input data-testid="sku-source" value={form.source_link} onChange={(e) => set("source_link", e.target.value)} placeholder="https://…" /></Field>
              </div>

              <div style={{ marginTop: 16 }}>
                <span className="lf-eyebrow">Washes</span>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10 }}>
                  {WASHES.map((w) => <Checkbox key={w} label={w} checked={form.washes.includes(w)} onChange={() => toggleWash(w)} />)}
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <span className="lf-eyebrow">Photos</span>
                <label data-testid="sku-photo-upload" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 10, padding: "16px", borderRadius: "var(--radius-md)", border: "1px dashed var(--border-strong)", background: "var(--surface-muted)", cursor: uploading ? "wait" : "pointer" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--lf-blue-600)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M20 16v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3" /></svg>
                  <span style={{ font: "var(--fw-medium) 13px/1.35 var(--font-sans)", color: "var(--text-secondary)" }}>{uploading ? "Uploading…" : "Upload garment photos from your computer (up to 4)"}</span>
                  <input type="file" accept="image/*" multiple disabled={uploading} onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10, marginTop: 10 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ position: "relative", aspectRatio: "3 / 4", borderRadius: "var(--radius-md)", overflow: "hidden", background: "var(--surface-inset)", backgroundImage: "var(--texture-weave-dark)" }}>
                        {form.images[i] ? <img src={form.images[i]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", font: "var(--fw-medium) 11px/1 var(--font-sans)", color: "var(--text-tertiary)" }}>Photo {i + 1}</span>}
                        {form.images[i] && <button type="button" onClick={() => setImg(i, "")} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", border: "none", background: "rgba(20,19,28,0.7)", color: "#fff", cursor: "pointer", lineHeight: 1 }}>×</button>}
                      </div>
                      <Input size="sm" value={form.images[i]} onChange={(e) => setImg(i, e.target.value)} placeholder={`or paste URL ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <span className="lf-eyebrow">Size chart</span>
                <label data-testid="size-chart-upload" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 10, padding: "16px", borderRadius: "var(--radius-md)", border: "1px dashed var(--border-strong)", background: "var(--surface-muted)", cursor: chartBusy ? "wait" : "pointer" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--lf-blue-600)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" /></svg>
                  <span style={{ font: "var(--fw-medium) 13px/1.35 var(--font-sans)", color: "var(--text-secondary)" }}>{chartBusy ? "Reading measurements…" : "Upload a size-chart screenshot — we'll read the table automatically"}</span>
                  <input type="file" accept="image/*" disabled={chartBusy} onChange={(e) => { parseChart(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
                </label>
                {form.size_chart && form.size_chart.columns && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                      <span style={{ font: "var(--fw-medium) 12px/1.3 var(--font-sans)", color: "var(--text-secondary)" }}>{form.size_chart.rows.length} sizes · {form.size_chart.columns.length} columns{form.size_chart.unit ? ` · ${form.size_chart.unit}` : ""}</span>
                      <button type="button" data-testid="size-chart-remove" onClick={() => set("size_chart", null)} style={{ background: "none", border: "none", color: "var(--lf-red-500)", cursor: "pointer", fontWeight: 600, font: "var(--fw-semibold) 12px/1 var(--font-sans)" }}>Remove</button>
                    </div>
                    <SizeChartTable chart={form.size_chart} />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <Button data-testid="save-sku-btn" variant="primary" size="md" onClick={save} disabled={busy}>{busy ? "Saving…" : editingId ? "Update SKU" : "Create SKU"}</Button>
                {editingId && <Button variant="ghost" size="md" onClick={resetForm}>Cancel</Button>}
              </div>
            </Panel>

            <Panel style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--fw-regular) 13px/1.4 var(--font-sans)" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-muted)", textAlign: "left" }}>
                      {["Code", "Name", "Status", "Segment", "Fabric", "Weight", "Price", "MOQ", "Source", "Catalogue", "Manufacturer", ""].map((h) => (
                        <th key={h} style={{ padding: "12px 14px", font: "var(--fw-semibold) 11px/1 var(--font-sans)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {skus.map((s) => (
                      <tr key={s.id} data-testid={`admin-sku-row-${s.code}`} style={{ borderTop: "1px solid var(--border-default)", background: s.is_disabled ? "var(--surface-muted)" : "transparent", color: s.is_disabled ? "var(--text-tertiary)" : undefined, transition: "background-color 0.2s" }}>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{s.code}</td>
                        <td style={{ padding: "12px 14px" }}>{s.name}</td>
                        <td style={{ padding: "12px 14px" }}>
                          <span data-testid={`sku-status-${s.code}`} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, font: "var(--fw-semibold) 11px/1.4 var(--font-sans)", background: s.is_disabled ? "#4B5563" : "rgba(22,163,74,0.12)", color: s.is_disabled ? "#FFFFFF" : "#15803D", whiteSpace: "nowrap" }}>{s.is_disabled ? "Disabled" : "Live"}</span>
                        </td>
                        <td style={{ padding: "12px 14px" }}>{s.segment}</td>
                        <td style={{ padding: "12px 14px" }}>{s.fabric}</td>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)" }}>{s.weight}</td>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)" }}>{s.priceLabel}</td>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)" }}>{s.moqLabel}</td>
                        <td style={{ padding: "12px 14px" }}>{s.source_link ? <a href={s.source_link} target="_blank" rel="noreferrer" style={{ color: "var(--lf-blue-600)", fontWeight: 600 }}>Source ↗</a> : "—"}</td>
                        <td style={{ padding: "12px 14px" }}>{CAT_LABEL[s.catalogue] || "Both"}</td>
                        <td style={{ padding: "12px 14px" }}>{manuById[s.manufacturer_id]?.name || <span style={{ color: "var(--text-tertiary)" }}>Unassigned</span>}</td>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <button data-testid={`edit-sku-${s.code}`} onClick={() => editSku(s)} style={{ marginRight: 10, background: "none", border: "none", color: "var(--lf-blue-600)", cursor: "pointer", fontWeight: 600 }}>Edit</button>
                          <button data-testid={`toggle-sku-${s.code}`} onClick={() => toggleDisabled(s)} style={{ marginRight: 10, background: "none", border: "none", color: s.is_disabled ? "#15803D" : "var(--text-secondary)", cursor: "pointer", fontWeight: 600 }}>{s.is_disabled ? "Enable" : "Disable"}</button>
                          <button data-testid={`delete-sku-${s.code}`} onClick={() => del(s)} style={{ background: "none", border: "none", color: "var(--lf-red-500)", cursor: "pointer", fontWeight: 600 }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}

        {tab === "manufacturers" && (
          <>
            <Panel>
              <div style={{ font: "var(--fw-semibold) 16px/1.2 var(--font-display)", color: "var(--text-primary)", marginBottom: 6 }}>{manuEditingId ? "Edit manufacturer" : "Add a manufacturer"}</div>
              <div style={{ font: "var(--fw-regular) 13px/1.4 var(--font-sans)", color: "var(--text-secondary)", marginBottom: 16 }}>India requires a unique GST; Bangladesh requires a unique BIN. Styles are linked to a manufacturer from the Catalogue tab.</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 14 }}>
                <Field label="Manufacturer name"><Input data-testid="manu-name" value={manuForm.name} onChange={(e) => setM("name", e.target.value)} placeholder="Blue Loom Apparels" /></Field>
                <Field label="Country"><Select data-testid="manu-country" options={COUNTRIES} value={manuForm.country} onChange={(e) => setM("country", e.target.value)} /></Field>
                {manuForm.country === "India"
                  ? <Field label="GST no. (unique)"><Input data-testid="manu-gst" value={manuForm.gst} onChange={(e) => setM("gst", e.target.value)} placeholder="27ABCDE1234F1Z5" /></Field>
                  : <Field label="BIN no. (unique)"><Input data-testid="manu-bin" value={manuForm.bin} onChange={(e) => setM("bin", e.target.value)} placeholder="123456789-0123" /></Field>}
                <Field label="Mobile"><Input data-testid="manu-mobile" type="tel" value={manuForm.mobile} onChange={(e) => setM("mobile", e.target.value)} placeholder="+91 98765 43210" /></Field>
                <Field label="Email"><Input data-testid="manu-email" type="email" value={manuForm.email} onChange={(e) => setM("email", e.target.value)} placeholder="ops@bluelooms.com" /></Field>
                <Field label="Address"><Input data-testid="manu-address" value={manuForm.address} onChange={(e) => setM("address", e.target.value)} placeholder="Plot 14, GIDC, Ahmedabad" /></Field>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <Button data-testid="save-manu-btn" variant="primary" size="md" onClick={saveManu} disabled={manuBusy}>{manuBusy ? "Saving…" : manuEditingId ? "Update manufacturer" : "Add manufacturer"}</Button>
                {manuEditingId && <Button variant="ghost" size="md" onClick={resetManu}>Cancel</Button>}
              </div>
            </Panel>

            <Panel style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--fw-regular) 13px/1.4 var(--font-sans)" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-muted)", textAlign: "left" }}>
                      {["Name", "Country", "GST / BIN", "Mobile", "Email", "Address", "Styles", ""].map((h) => (
                        <th key={h} style={{ padding: "12px 14px", font: "var(--fw-semibold) 11px/1 var(--font-sans)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {manufacturers.length === 0 && <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: "var(--text-tertiary)" }}>No manufacturers yet.</td></tr>}
                    {manufacturers.map((m) => (
                      <tr key={m.id} data-testid={`manu-row-${m.id}`} style={{ borderTop: "1px solid var(--border-default)", verticalAlign: "top" }}>
                        <td style={{ padding: "12px 14px", fontWeight: 600 }}>{m.name}</td>
                        <td style={{ padding: "12px 14px" }}>{m.country}</td>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)" }}>{m.gst || m.bin || "—"}</td>
                        <td style={{ padding: "12px 14px" }}>{m.mobile}</td>
                        <td style={{ padding: "12px 14px" }}>{m.email}</td>
                        <td style={{ padding: "12px 14px", maxWidth: 200, color: "var(--text-secondary)" }}>{m.address || "—"}</td>
                        <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)" }}>{skus.filter((s) => s.manufacturer_id === m.id).length}</td>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <button data-testid={`edit-manu-${m.id}`} onClick={() => editManu(m)} style={{ marginRight: 10, background: "none", border: "none", color: "var(--lf-blue-600)", cursor: "pointer", fontWeight: 600 }}>Edit</button>
                          <button data-testid={`delete-manu-${m.id}`} onClick={() => delManu(m)} style={{ background: "none", border: "none", color: "var(--lf-red-500)", cursor: "pointer", fontWeight: 600 }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}

        {tab === "leads" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ font: "var(--fw-regular) 13px/1.5 var(--font-sans)", color: "var(--text-secondary)" }}>
                All enquiries land here — review them anytime. Automatic email alerts are off.
              </div>
            </div>
            <Panel style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--fw-regular) 13px/1.4 var(--font-sans)" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-muted)", textAlign: "left" }}>
                      {["When", "Category", "Name", "Company", "Contact", "SKU", "Type", "Qty / Wash", "Notes"].map((h) => (
                        <th key={h} style={{ padding: "12px 14px", font: "var(--fw-semibold) 11px/1 var(--font-sans)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "var(--text-tertiary)" }}>No enquiries yet.</td></tr>}
                    {leads.map((l) => (
                      <tr key={l.id} data-testid="admin-lead-row" style={{ borderTop: "1px solid var(--border-default)", verticalAlign: "top" }}>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap", color: "var(--text-tertiary)" }}>{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td style={{ padding: "12px 14px" }}>
                          <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: "var(--radius-pill)", font: "var(--fw-semibold) 10px/1.4 var(--font-sans)", color: "#fff", background: l.lead_type === "SKU lead" ? "var(--lf-blue-600)" : "var(--lf-orange-500)" }}>{l.lead_type || (l.sku_code || l.sku_name ? "SKU lead" : "Generic lead")}</span>
                        </td>
                        <td style={{ padding: "12px 14px" }}>{l.name}</td>
                        <td style={{ padding: "12px 14px" }}>{l.company || "—"}{l.company_type ? <><br /><span style={{ color: "var(--text-tertiary)", font: "var(--fw-regular) 11px/1.3 var(--font-sans)" }}>{l.company_type}</span></> : ""}</td>
                        <td style={{ padding: "12px 14px" }}>{l.email}<br /><span style={{ color: "var(--text-tertiary)" }}>{l.phone}</span></td>
                        <td style={{ padding: "12px 14px" }}>{l.sku_name || l.sku_code || <span style={{ color: "var(--text-tertiary)" }}>Generic</span>}</td>
                        <td style={{ padding: "12px 14px" }}>{l.enquiry_type || l.intent}</td>
                        <td style={{ padding: "12px 14px" }}>{l.quantity || "—"} {l.wash ? `· ${l.wash}` : ""}</td>
                        <td style={{ padding: "12px 14px", maxWidth: 240, color: "var(--text-secondary)" }}>{l.size_ratio || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}
